import {api} from "../api.slice";
import {
    CreateFactorCheckRequest,
    FactorCheck,
    GetFactorCheckItemsRequest,
    GetFactorCheckItemsResponse,
    GetFactorChecksRequest,
    GetFactorChecksResponse,
    GetFactorsRequest,
    GetFactorsResponse,
    GetTransportAccountsResponse,
    SendFactorCheckInfoRequest,
    UpdateFactorCheckItemsCountRequest,
    UpdateFactorCheckRequest
} from "@/types/factor";
import {apiEndpoints} from "@/utils/api-endpoints";

const factorCheckApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getFactorChecks: builder.query<GetFactorChecksResponse, GetFactorChecksRequest>({
            query: (arg) => ({
                url: apiEndpoints.FactorCheck.GetFactorChecks.url,
                method: apiEndpoints.FactorCheck.GetFactorChecks.method,
                params: arg,
            }),
            providesTags: (result) => {
                const rows: any[] = (result as any)?.data ?? [];

                return [
                    {type: "FactorChecks" as const, id: "LIST"},
                    ...rows.map((x) => ({type: "FactorChecks" as const, id: Number(x.id)})),
                ];
            },
        }),

        getAllFactors: builder.query<GetFactorsResponse, GetFactorsRequest>({
            query: (arg) => ({
                url: apiEndpoints.FactorCheck.GetFactors.url,
                method: apiEndpoints.FactorCheck.GetFactors.method,
                params: arg,
            }),
            providesTags: [{type: "Factors" as const, id: "LIST"}],
        }),

        getFactorCheck: builder.query<FactorCheck, number>({
            query: (id) => ({
                url: apiEndpoints.FactorCheck.GetFactorCheck.url(String(id)),
                method: apiEndpoints.FactorCheck.GetFactorCheck.method,
            }),
            providesTags: (result, error, id) => [{type: "FactorChecks" as const, id}],
        }),

        createNewFactorCheck: builder.mutation<number, CreateFactorCheckRequest>({
            query: (arg) => ({
                url: apiEndpoints.FactorCheck.Add.url,
                method: apiEndpoints.FactorCheck.Add.method,
                body: {factorIds: arg.factorIds},
            }),
            invalidatesTags: [{type: "FactorChecks" as const, id: "LIST"}],
        }),

        getFactorCheckItems: builder.query<GetFactorCheckItemsResponse, GetFactorCheckItemsRequest>({
            query: (arg) => ({
                url: apiEndpoints.FactorCheck.GetItems.url(String(arg.factorCheckId)),
                method: apiEndpoints.FactorCheck.GetItems.method,
                params: {
                    page: arg.page,
                    viewMode: arg.viewMode,
                    searchText: arg.searchText,
                },
            }),
            providesTags: (r, e, arg) => [{type: "FactorCheckItems" as const, id: arg.factorCheckId}],
        }),

        updateFactorCheckItemsCount: builder.mutation<
            void,
            UpdateFactorCheckItemsCountRequest
        >({
            query: (arg) => ({
                url: apiEndpoints.FactorCheck.EditItemCount.url(
                    String(arg.factorCheckId)
                ),
                method: apiEndpoints.FactorCheck.EditItemCount.method,
                body: {
                    count: arg.count,
                    productId: arg.itemId,
                },
            }),

            async onQueryStarted(
                arg,
                {dispatch, getState, queryFulfilled}
            ) {
                const cachedQueries = factorCheckApi.util.selectInvalidatedBy(
                    getState(),
                    [
                        {
                            type: "FactorCheckItems",
                            id: arg.factorCheckId,
                        },
                    ]
                );

                const patches = cachedQueries
                    .filter(
                        ({endpointName}) =>
                            endpointName === "getFactorCheckItems"
                    )
                    .map(({originalArgs}) =>
                        dispatch(
                            factorCheckApi.util.updateQueryData(
                                "getFactorCheckItems",
                                originalArgs as GetFactorCheckItemsRequest,
                                (draft) => {
                                    const item = draft.data.find(
                                        item => item.productId === arg.itemId
                                    );

                                    if (item) {
                                        item.checkedCount = arg.count;
                                    }
                                }
                            )
                        )
                    );

                try {
                    await queryFulfilled;
                } catch {
                    patches.forEach(patch => patch.undo());
                }
            },

            invalidatesTags: (result, error, arg) =>
                error
                    ? []
                    : [
                        {
                            type: "FactorCheckItems" as const,
                            id: arg.factorCheckId,
                        },
                    ],
        }),
        getFactorChecksFactors: builder.query<GetFactorsResponse, number>({
            query: (id) => ({
                url: apiEndpoints.FactorCheck.GetFactorsId.url(String(id)),
                method: apiEndpoints.FactorCheck.GetFactorsId.method,
            }),
            providesTags: (r, e, id) => [{type: "FactorChecks" as const, id}],
        }),

        updateFactorCheck: builder.mutation<void, UpdateFactorCheckRequest>({
            query: (arg) => ({
                url: apiEndpoints.FactorCheck.Edit.url(String(arg.factorCheckId)),
                method: apiEndpoints.FactorCheck.Edit.method,
                params: {packingDescription: arg.packingDescription},
            }),
            invalidatesTags: (r, e, arg) => [{type: "FactorChecks" as const, id: arg.factorCheckId}],
            async onQueryStarted({factorCheckId, packingDescription}, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;
                    dispatch(
                        factorCheckApi.util.updateQueryData("getFactorCheck", factorCheckId, (draft) => {
                            (draft as any).packingDescription = packingDescription;
                        })
                    );
                } catch {
                }
            },
        }),

        setFactorCheckFactors: builder.mutation<void, { factorCheckId: number; factorIds: number[] }>({
            query: (arg) => ({
                url: apiEndpoints.FactorCheck.EditFactors.url(String(arg.factorCheckId)),
                method: apiEndpoints.FactorCheck.EditFactors.method,
                body: {factorIds: arg.factorIds},
            }),
            invalidatesTags: (r, e, arg) => [
                {type: "FactorChecks" as const, id: arg.factorCheckId},
                {type: "FactorCheckItems" as const, id: arg.factorCheckId},
            ],
        }),

        removeFactorCheck: builder.mutation<void, number>({
            query: (id) => ({
                url: apiEndpoints.FactorCheck.Delete.url(String(id)),
                method: apiEndpoints.FactorCheck.Delete.method,
            }),

            invalidatesTags: [{type: "FactorChecks" as const, id: "LIST"}],
        }),

        sendFactorCheck: builder.mutation<void, number>({
            query: (id) => ({
                url: apiEndpoints.FactorCheck.Send.url(String(id)),
                method: apiEndpoints.FactorCheck.Send.method,
            }),
            invalidatesTags: (r, e, id) => [
                {type: "FactorChecks" as const, id: "LIST"},
                {type: "FactorChecks" as const, id},
            ],
        }),

        finishFactorCheck: builder.mutation<void, { id: number; packingDescription: string }>({
            query: (arg) => ({
                url: apiEndpoints.FactorCheck.Finish.url(String(arg.id)),
                method: apiEndpoints.FactorCheck.Finish.method,
                params: {packingDescription: arg.packingDescription},
            }),
            invalidatesTags: (r, e, arg) => [
                {type: "FactorChecks" as const, id: "LIST"},
                {type: "FactorChecks" as const, id: arg.id},
                {type: "FactorCheckItems" as const, id: arg.id},
            ],
        }),
        sendFactorCheckInfo: builder.mutation<void, SendFactorCheckInfoRequest>({
            query: ({factorCheckId, formData}) => ({
                url: apiEndpoints.FactorCheck.Send.url(String(factorCheckId)),
                method: apiEndpoints.FactorCheck.Send.method,
                body: formData,
            }),
            invalidatesTags: (r, e, arg) => [
                {type: "FactorChecks" as const, id: "LIST"},
                {type: "FactorChecks" as const, id: arg.factorCheckId},
            ],
        }),
        restoreFactorCheck: builder.mutation<void, number>({
            query: (id) => ({
                url: apiEndpoints.FactorCheck.Restore.url(String(id)),
                method: apiEndpoints.FactorCheck.Restore.method,
            }),
            invalidatesTags: (r, e, id) => [
                {type: "FactorChecks" as const, id: "LIST"},
            ],
        }),
        getTransportAccounts: builder.query<GetTransportAccountsResponse, { searchText?: string }>({
            query: (arg) => ({
                url: apiEndpoints.FactorCheck.GetTransportAccounts.url,
                method: apiEndpoints.FactorCheck.GetTransportAccounts.method,
                params: {
                    searchText: arg.searchText ?? "",
                },
            }),
        }),
        updateFactorCheckTransport: builder.mutation<void, { factorCheckId: number; formData: FormData }>({
            query: ({factorCheckId, formData}) => ({
                url: apiEndpoints.FactorCheck.UpdateTransport.url(
                    String(factorCheckId)
                ),
                method: apiEndpoints.FactorCheck.UpdateTransport.method,
                body: formData,
            }),
            invalidatesTags: (r, e, id) => [
                {type: "FactorChecks" as const, id: "LIST"},
            ],
        }),
        deleteFactorCheckTransport: builder.mutation<void, number>({
            query: (id) => ({
                url: apiEndpoints.FactorCheck.DeleteTransport.url(String(id)),
                method: apiEndpoints.FactorCheck.DeleteTransport.method,
            }),
            invalidatesTags: (r, e, id) => [
                {type: "FactorChecks" as const, id: "LIST"},
                {type: "FactorChecks" as const, id},
            ],
        }),
    }),
});

export const {
    useGetFactorChecksQuery,
    useGetAllFactorsQuery,
    useCreateNewFactorCheckMutation,
    useGetFactorCheckItemsQuery,
    useUpdateFactorCheckItemsCountMutation,
    useGetFactorChecksFactorsQuery,
    useUpdateFactorCheckMutation,
    useGetFactorCheckQuery,
    useSetFactorCheckFactorsMutation,
    useRemoveFactorCheckMutation,
    useSendFactorCheckMutation,
    useFinishFactorCheckMutation,
    useSendFactorCheckInfoMutation,
    useRestoreFactorCheckMutation,
    useGetTransportAccountsQuery,
    useUpdateFactorCheckTransportMutation,
    useDeleteFactorCheckTransportMutation,
} = factorCheckApi;

export default factorCheckApi;
